import React, { useMemo, useRef } from 'react';
import { BellIcon, XIcon, TrashIcon, CheckIcon } from '../icons';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useNotificationsContext, type AppNotification } from '../../contexts/NotificationsContext';
import { isPrivilegedRole } from '../../services/modules/notifications/notification.visibility';
import { useAppContext } from '../../contexts/AppContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAuth } from '../../contexts/AuthContext';

interface NotificationModalProps {
    isOpen: boolean;
    onClose: () => void;
    triggerRef?: React.RefObject<HTMLElement>;
}

interface UserGroup {
    key: string;
    userName: string;
    items: AppNotification[];
}

interface OrgGroup {
    orgId: string;
    orgName: string;
    showUserGroups: boolean;
    users: UserGroup[];
    flatItems: AppNotification[];
}

function formatNotificationTime(date: Date): string {
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return `Hoje às ${time}`;
    return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export const NotificationModal: React.FC<NotificationModalProps> = ({ isOpen, onClose, triggerRef }) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const {
        visibleNotifications,
        markAsRead,
        removeNotification,
        markAllAsRead,
        clearAllNotifications,
    } = useNotificationsContext();
    const { organizations } = useOrganization();
    const { user } = useAuth();
    const { setCurrentPage } = useAppContext();

    useClickOutside(modalRef, onClose, isOpen, triggerRef ? [triggerRef] : []);

    const grouped = useMemo((): OrgGroup[] => {
        const roleByOrg = new Map(organizations.map((o) => [o.id, o.role]));
        const orgMap = new Map<string, OrgGroup>();

        for (const notification of visibleNotifications) {
            const orgId = notification.organizationId || 'general';
            const orgName =
                notification.organizationName
                || organizations.find((o) => o.id === notification.organizationId)?.name
                || 'Geral';
            const showUserGroups = orgId === 'general'
                ? false
                : isPrivilegedRole(roleByOrg.get(orgId));

            if (!orgMap.has(orgId)) {
                orgMap.set(orgId, {
                    orgId,
                    orgName,
                    showUserGroups,
                    users: [],
                    flatItems: [],
                });
            }

            const orgGroup = orgMap.get(orgId)!;

            if (!showUserGroups) {
                orgGroup.flatItems.push(notification);
                continue;
            }

            const userKey = notification.recipientUserId || 'unknown';
            const userName =
                notification.recipientUserId === user?.id
                    ? 'Você'
                    : (notification.recipientUserName || 'Usuário');

            let userGroup = orgGroup.users.find((u) => u.key === userKey);
            if (!userGroup) {
                userGroup = { key: userKey, userName, items: [] };
                orgGroup.users.push(userGroup);
            }
            userGroup.items.push(notification);
        }

        return Array.from(orgMap.values());
    }, [visibleNotifications, organizations, user?.id]);

    if (!isOpen) return null;

    const handleNotificationClick = (notification: AppNotification) => {
        markAsRead(notification.id);
        if (notification.link?.includes('/tasks') || notification.link?.includes('/tarefas')) {
            setCurrentPage('tasks');
            try {
                const u = new URL(notification.link, window.location.origin);
                const open = u.searchParams.get('open');
                if (open) {
                    window.history.pushState(null, '', `/tarefas?open=${encodeURIComponent(open)}`);
                    window.dispatchEvent(new CustomEvent('adlitics:tasks-open', { detail: { taskId: open } }));
                }
            } catch { /* ignore */ }
        }
        onClose();
    };

    const renderNotification = (notification: AppNotification) => (
        <article
            key={notification.id}
            data-testid="notification-item"
            data-read={notification.read ? 'true' : 'false'}
            data-org-id={notification.organizationId || 'general'}
            className="group relative border-b border-subtle bg-card px-4 py-3 transition-colors last:border-b-0 hover:bg-[color-mix(in_srgb,var(--color-surface-subtle)_50%,transparent)]"
        >
            <div className="flex gap-3">
                <div
                    className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${!notification.read ? 'bg-primary' : 'bg-transparent'}`}
                    aria-hidden
                />

                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => handleNotificationClick(notification)}>
                    <p className={`text-sm leading-snug ${!notification.read ? 'font-medium text-default' : 'text-secondary'}`}>
                        {notification.title}
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted">
                        {notification.message}
                    </p>
                    <time className="mt-1.5 block text-[11px] text-muted">
                        {formatNotificationTime(notification.timestamp)}
                    </time>
                </div>

                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeNotification(notification.id); }}
                    className="absolute right-2 top-2 rounded-md p-1 text-muted opacity-0 transition-all hover:bg-surface-subtle hover:text-error group-hover:opacity-100"
                    title="Remover"
                >
                    <XIcon className="h-3.5 w-3.5" />
                </button>
            </div>
        </article>
    );

    return (
        <div
            ref={modalRef}
            className="absolute top-full right-0 z-dropdown mt-2 flex max-h-[28rem] w-96 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-subtle bg-card shadow-lg"
            data-testid="notification-dropdown"
            role="dialog"
            aria-label="Notificações"
        >
            <header className="flex shrink-0 items-center justify-between border-b border-subtle px-4 py-3">
                <h3 className="metric-label">Notificações</h3>
                <div className="flex items-center gap-0.5">
                    {visibleNotifications.length > 0 && (
                        <>
                            <button
                                type="button"
                                onClick={markAllAsRead}
                                className="rounded-md p-1.5 text-muted transition-colors hover:bg-[color-mix(in_srgb,var(--color-surface-subtle)_50%,transparent)] hover:text-default"
                                title="Marcar todas como lidas"
                            >
                                <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={clearAllNotifications}
                                className="rounded-md p-1.5 text-muted transition-colors hover:bg-red-50 hover:text-error"
                                title="Limpar notificações visíveis"
                            >
                                <TrashIcon className="h-4 w-4" />
                            </button>
                        </>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md p-1.5 text-muted transition-colors hover:bg-[color-mix(in_srgb,var(--color-surface-subtle)_50%,transparent)] hover:text-default"
                        title="Fechar"
                    >
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>
            </header>

            <div className="flex-1 overflow-y-auto bg-card">
                {visibleNotifications.length === 0 ? (
                    <div
                        className="flex flex-col items-center justify-center px-6 py-10 text-center"
                        data-testid="notification-empty"
                    >
                        <BellIcon className="mb-3 h-5 w-5 text-muted" aria-hidden />
                        <h4 className="text-sm font-medium text-default">Nenhuma notificação</h4>
                        <p className="mt-1 max-w-[220px] text-xs leading-relaxed text-muted">
                            Alertas de tarefas e atualizações da equipe aparecerão aqui.
                        </p>
                    </div>
                ) : (
                    <div>
                        {grouped.map((orgGroup) => (
                            <section
                                key={orgGroup.orgId}
                                data-testid="notification-org-group"
                                aria-label={`Notificações de ${orgGroup.orgName}`}
                            >
                                <div className="sticky top-0 z-raised border-b border-subtle bg-[color-mix(in_srgb,var(--color-surface-subtle)_80%,transparent)] px-4 py-2">
                                    <p className="metric-label">{orgGroup.orgName}</p>
                                </div>

                                {orgGroup.showUserGroups
                                    ? orgGroup.users.map((userGroup) => (
                                        <div key={userGroup.key}>
                                            <div className="border-b border-subtle px-4 py-1.5">
                                                <p className="text-[11px] font-medium text-muted">
                                                    {userGroup.userName}
                                                </p>
                                            </div>
                                            {userGroup.items.map(renderNotification)}
                                        </div>
                                    ))
                                    : orgGroup.flatItems.map(renderNotification)}
                            </section>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
